/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * CSV Export Utilities
 *
 * Standardized CSV export functionality for consistent data export across the application.
 * Handles proper escaping, formatting, and error handling.
 */

import { toIsoDateString } from "@/lib/utils/domain/date-helpers";

export interface CSVExportOptions {
  /** Custom filename for the export (without .csv extension) */
  filename?: string;
  /** Whether to include a timestamp in the filename */
  includeTimestamp?: boolean;
  /** Custom date formatter for timestamp */
  dateFormatter?: (date: Date) => string;
}

export interface CSVField<
  T extends Record<string, unknown> = Record<string, unknown>,
  K extends keyof T = keyof T,
> {
  /** The key to extract from the data object */
  key: K;
  /** The header label for this column */
  header: string;
  /** Custom formatter function for this field */
  formatter?: (value: T[K]) => string;
}

/**
 * Escapes a CSV field value to handle commas, quotes, and newlines
 */
function escapeCSVField(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }

  const stringValue = typeof value === "string" ? value : String(value);

  // If the value contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Converts an array of objects to CSV string using field definitions
 */
export function arrayToCSV<T extends Record<string, unknown>>(
  data: T[],
  fields: CSVField<T>[],
): string {
  if (data.length === 0) {
    throw new Error("No data provided for CSV export");
  }

  // Create header row
  const headers = fields.map((field) => escapeCSVField(field.header));
  const headerRow = headers.join(",");

  // Create data rows
  const dataRows = data.map((item) => {
    const row = fields.map((field) => {
      const value = item[field.key];
      const formattedValue = field.formatter ? field.formatter(value) : value;
      return escapeCSVField(formattedValue);
    });
    return row.join(",");
  });

  return [headerRow, ...dataRows].join("\n");
}

/**
 * Converts an array of objects to CSV using object keys as headers
 */
export function objectArrayToCSV<T extends Record<string, unknown>>(
  data: T[],
  headerMap?: Partial<Record<keyof T, string>>,
): string {
  if (data.length === 0) {
    throw new Error("No data provided for CSV export");
  }

  const firstRow = data[0];
  if (!firstRow) {
    throw new Error("First row is empty");
  }

  const keys = Object.keys(firstRow) as (keyof T)[];

  // Create headers (use provided map or keys as-is)
  const headers = keys.map((key) => {
    const headerText = headerMap?.[key] ?? String(key);
    return escapeCSVField(headerText);
  });

  const headerRow = headers.join(",");

  // Create data rows
  const dataRows = data.map((item) => {
    const row = keys.map((key) => escapeCSVField(item[key]));
    return row.join(",");
  });

  return [headerRow, ...dataRows].join("\n");
}

/**
 * Generates a filename with optional timestamp
 */
export function generateCSVFilename(
  baseName: string,
  options: CSVExportOptions = {},
): string {
  const { includeTimestamp = true, dateFormatter } = options;

  let filename = baseName;

  if (includeTimestamp) {
    const timestamp = dateFormatter
      ? dateFormatter(new Date())
      : toIsoDateString(new Date());
    filename = `${baseName}_${timestamp}`;
  }

  return `${filename}.csv`;
}

/**
 * Common formatters for typical data types
 */
export const CSVFormatters = {
  /** Format currency values */
  currency: (value: number | null | undefined): string => {
    if (typeof value !== "number") return "";
    return `$${value.toLocaleString()}`;
  },

  /** Format dates to locale string */
  date: (value: string | Date | null | undefined): string => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString();
  },

  /** Format dates to ISO string */
  dateISO: (value: string | Date | null | undefined): string => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? "" : date.toISOString();
  },

  /** Format boolean values */
  boolean: (value: boolean | null | undefined): string => {
    if (value === null || value === undefined) return "";
    return value ? "Yes" : "No";
  },

  /** Format status with title case */
  status: (value: string | null | undefined): string => {
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  },

  /** Safe text formatter that handles null/undefined */
  text: (value: unknown): string => {
    if (value === null || value === undefined) return "";
    return String(value);
  },
};

/**
 * Pre-configured field definitions for common data types
 */
export const CommonCSVFields = {
  /** Standard client fields */
  client: {
    id: {
      key: "client_id" as const,
      header: "Client ID",
      formatter: CSVFormatters.text,
    },
    name: {
      key: "name" as const,
      header: "Client Name",
      formatter: CSVFormatters.text,
    },
    email: {
      key: "email" as const,
      header: "Email",
      formatter: CSVFormatters.text,
    },
    phone: {
      key: "phone" as const,
      header: "Phone",
      formatter: CSVFormatters.text,
    },
    status: {
      key: "status" as const,
      header: "Status",
      formatter: CSVFormatters.status,
    },
    created: {
      key: "created_at" as const,
      header: "Created Date",
      formatter: CSVFormatters.date,
    },
  },

  /** Standard project fields */
  project: {
    id: {
      key: "project_id" as const,
      header: "Project ID",
      formatter: CSVFormatters.text,
    },
    name: {
      key: "name" as const,
      header: "Project Name",
      formatter: CSVFormatters.text,
    },
    description: {
      key: "description" as const,
      header: "Description",
      formatter: CSVFormatters.text,
    },
    status: {
      key: "status" as const,
      header: "Status",
      formatter: CSVFormatters.status,
    },
    location: {
      key: "location" as const,
      header: "Location",
      formatter: CSVFormatters.text,
    },
    budget: {
      key: "budget" as const,
      header: "Budget",
      formatter: CSVFormatters.currency,
    },
    created: {
      key: "created_at" as const,
      header: "Created Date",
      formatter: CSVFormatters.date,
    },
  },

  /** Standard estimate fields */
  estimate: {
    id: {
      key: "estimate_id" as const,
      header: "Estimate ID",
      formatter: CSVFormatters.text,
    },
    number: {
      key: "estimate_number" as const,
      header: "Estimate Number",
      formatter: CSVFormatters.text,
    },
    title: {
      key: "title" as const,
      header: "Title",
      formatter: CSVFormatters.text,
    },
    status: {
      key: "status" as const,
      header: "Status",
      formatter: CSVFormatters.status,
    },
    total: {
      key: "total_amount" as const,
      header: "Total Amount",
      formatter: CSVFormatters.currency,
    },
    description: {
      key: "description" as const,
      header: "Description",
      formatter: CSVFormatters.text,
    },
    created: {
      key: "created_at" as const,
      header: "Created Date",
      formatter: CSVFormatters.date,
    },
  },

  /** Standard proposal fields */
  proposal: {
    id: {
      key: "proposal_id" as const,
      header: "Proposal ID",
      formatter: CSVFormatters.text,
    },
    title: {
      key: "title" as const,
      header: "Title",
      formatter: CSVFormatters.text,
    },
    status: {
      key: "status" as const,
      header: "Status",
      formatter: CSVFormatters.status,
    },
    total: {
      key: "total_amount" as const,
      header: "Total Amount",
      formatter: CSVFormatters.currency,
    },
    created: {
      key: "created_at" as const,
      header: "Created Date",
      formatter: CSVFormatters.date,
    },
  },
};
